import React, {
  useRef, useState, useMemo, memo
} from 'react'
import styled, { css } from 'styled-components/macro'
import { Icon, ScreenReader, LoadingSpinner } from 'components/atoms'
import { darken } from 'polished';
import { useOnClickOutside } from 'hooks';
import { iconType } from 'types';
import PropTypes from 'prop-types'
import { generateKey } from 'services/generators'
import { applyStyleModifiers } from 'styled-components-modifiers';

const MODIFIERS = {
  stretch: () => `
    width: 100%;
  `
};

const InputIcon = memo(({
  icon, onChange, value, placeholder, label, isLoading,
}) => {
  const inputRef = useRef(null);
  const [isFocus, setFocus] = useState(false)

  const setFocused = shouldFocus => {
    setFocus(shouldFocus);
    if (shouldFocus && inputRef && inputRef.current) inputRef.current.focus();
  };

  const id = useMemo(() => generateKey('input-label_'), [])

  useOnClickOutside(inputRef, () => setFocused(false));

  return (
    <Input
      onClick={() => setFocused(true)}
      isFocus={isFocus}
      type="button"
    >
      {(label || placeholder) && (
        <ScreenReader as="label" htmlFor={id}>
          {label || placeholder}
        </ScreenReader>
      )}
      {icon && (
        <Input.Icon isLoading={isLoading}>
          <Icon icon={icon} />
        </Input.Icon>
      )}
      <Input.Loading isLoading={isLoading}>
        <LoadingSpinner />
      </Input.Loading>
      <Input.Field
        ref={inputRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        id={id}
      />
    </Input>
  )
})

const Input = styled.div`
  display: flex;
  padding: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.bgLighterS};
  cursor: text;
  position: relative;

  &:hover {
      background-color: ${props => darken(0.04, props.theme.colors.bgLighterS)};
      color: ${props => props.theme.colors.base};
    }
  &:focus {
    outline: 0;
    color: ${props => props.theme.colors.base};
    background-color: ${props => darken(0.03, props.theme.colors.bgLighterS)};
  }
  &:active {
    color: ${props => props.theme.colors.base};
    background-color: ${props => props.theme.colors.bgLighterS};
  }

  ${props => (props.isFocus
    ? css`
        outline: 0;
        color: ${props => props.theme.colors.base};
        background-color: ${props => darken(0.03, props.theme.colors.bgLighterS)};
      `
    : '')}

  ${applyStyleModifiers(MODIFIERS)};
`

Input.Icon = styled.div`
  opacity: ${props => (props.isLoading ? 0 : 1)};
  padding-right: ${props => props.theme.spacing.xs};
`

Input.Loading = styled.div`
  opacity: ${props => (props.isLoading ? 1 : 0)};
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`

Input.Field = styled.input`
  border: none;
  font-size: ${props => props.theme.sizes.m};
  width: 100%;
  background-color: transparent;

  &:focus {
    outline: none;
  }
`

InputIcon.propTypes = {
  icon: iconType,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  isLoading: PropTypes.bool,
}

InputIcon.defaultProps = {
  icon: null,
  value: '',
  onChange: () => { },
  placeholder: '',
  label: null,
  isLoading: false,
}


export default InputIcon
